import { setupLayouts } from 'virtual:generated-layouts'
import { createRouter, createWebHistory } from 'vue-router/auto'
import { redirects, routes } from './additional-routes'
import { setupGuards } from './guards'

function recursiveLayouts(route) {
  if (route.children) {
    for (let i = 0; i < route.children.length; i++)
      route.children[i] = recursiveLayouts(route.children[i])

    return route
  }

  return setupLayouts([route])[0]
}

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to) {
    if (to.hash)
      return { el: to.hash, behavior: 'smooth', top: 60 }
    
    return { top: 0 }
  },
  extendRoutes: pages => [
    ...redirects,
    ...[
      ...pages,
      ...routes,
    ].map(route => recursiveLayouts(route)),
  ],
})

setupGuards(router)

// Chunk-load recovery: after a deploy, the user's already-loaded
// index.html still references the OLD Vite chunk hashes (e.g.
// `_containerId_-gmyrQfQ8.js`). When they click a sidebar link, Vue
// Router tries to lazy-load that old chunk → 404 → navigation silently
// fails and the sidebar appears "broken" until the user reloads.
//
// Detect the dynamic-import failure and force a one-time full reload
// so the browser picks up the fresh index.html and the new chunks.
// The `sessionStorage` guard prevents an infinite reload loop in the
// edge case where the network/server is actually unreachable.
const CHUNK_RELOAD_KEY = 'godana_chunk_reload_at'
const CHUNK_RELOAD_COOLDOWN_MS = 10_000

function isChunkLoadError(err) {
  const msg = err?.message || ''
  // Chrome: "Failed to fetch dynamically imported module"
  // Firefox: "error loading dynamically imported module"
  // Safari: "Importing a module script failed"
  return /dynamically imported module|Importing a module script failed|Loading chunk \w+ failed|ChunkLoadError/i.test(msg)
}

router.onError((err, to) => {
  if (!isChunkLoadError(err)) return
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0)
  if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) {
    // Already reloaded recently — don't loop. Likely a real network
    // issue rather than a stale-chunk situation.
    console.warn('[router] chunk load error within cooldown, skipping reload:', err)
    return
  }
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  // Reload to the URL the user was trying to reach so we end up where
  // they wanted to go after the browser picks up the new index.html.
  const targetUrl = to?.fullPath
    || (typeof window !== 'undefined' ? window.location.pathname + window.location.search + window.location.hash : '/')
  console.info('[router] reloading to recover from chunk-load error →', targetUrl)
  window.location.assign(targetUrl)
})

export { router }
export default function (app) {
  app.use(router)
}
